/**
 * IntelligenceHub.js - Phase 118: Simplified Tracking System
 * 
 * Consolidates all tracking features into one slide-out panel:
 * - Companies (corporate entities to monitor)
 * - Assets (drugs/targets being monitored)
 * - Topics (news keywords for Wire feed)
 * 
 * Phase 118: Removed confusing "Deals" tab, replaced with "Topics" for Wire clarity
 * Phase 115: Added contextual tooltips for complex metrics
 * Design: Scientific Minimalism with smart auto-categorization
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {companyDestination} from '@/utils/companyNavigation';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Star,
  Building2,
  Pill,
  Rss,
  AlertTriangle,
  Eye,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  ExternalLink,
  Search,
  X,
  Clock,
  ChevronRight,
  Flame,
  MoreHorizontal
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import InfoTooltip from "@/components/InfoTooltip";

// Tab definitions - Phase 118: Replaced "Deals" with "Topics" for Wire integration
const TABS = [
  { id: "companies", label: "Companies", icon: Building2 },
  { id: "assets", label: "Assets", icon: Pill },
  { id: "topics", label: "Topics", icon: Rss }
];

// Status groupings with priority order
// These categories help users prioritize which tracked items need attention
const STATUS_GROUPS = {
  critical: { 
    label: "Needs Attention", 
    sublabel: "Low runway or issues detected",
    icon: AlertTriangle, 
    color: "text-rose-600", 
    bg: "bg-rose-50", 
    border: "border-rose-200" 
  },
  watching: { 
    label: "Monitoring", 
    sublabel: "Active developments",
    icon: Eye, 
    color: "text-amber-600", 
    bg: "bg-amber-50", 
    border: "border-amber-200" 
  },
  stable: { 
    label: "On Track", 
    sublabel: "No concerns",
    icon: CheckCircle, 
    color: "text-emerald-600", 
    bg: "bg-emerald-50", 
    border: "border-emerald-200" 
  }
};

/**
 * Categorize item by its health/status
 * 
 * Companies: Based on cash runway
 *   - Critical: < 12 months runway
 *   - Watching: 12-24 months runway  
 *   - Stable: > 24 months or unknown
 * 
 * Assets: Based on trial status
 *   - Critical: Failed or suspended trials
 *   - Watching: Phase 2+ (active development)
 *   - Stable: Early stage or approved
 */
function categorizeItem(item, type) {
  if (type === "companies") {
    const runway = item.runway_months || item.cash_runway_months;
    if (runway && runway < 12) return "critical";
    if (runway && runway < 24) return "watching";
    return "stable";
  }
  if (type === "assets") {
    // Critical if trial failed or suspended
    if (item.status === "failed" || item.status === "suspended") return "critical";
    // Watching if in active phase
    if (item.phase >= 2) return "watching";
    return "stable";
  }
  // Topics don't have status - all are "stable" (actively filtering Wire)
  if (type === "topics") {
    return "stable";
  }
  return "stable";
}

/**
 * Individual tracked item row
 */
function TrackedItem({ item, type, onRemove, onNavigate }) {
  const navigate = useNavigate();
  const status = categorizeItem(item, type);
  const statusConfig = STATUS_GROUPS[status];
  
  const handleClick = () => {
    if (type === "companies") {
      navigate(companyDestination(item));
    } else if (type === "assets") {
      // Navigate to Research page with asset search
      const assetName = item.name || item.asset_name;
      navigate(`/research?q=${encodeURIComponent(assetName)}&type=asset`);
    } else if (type === "topics") {
      // Navigate to Monitor page with topic filter
      const topicName = item.name;
      navigate(`/monitor?topic=${encodeURIComponent(topicName)}`);
    }
    onNavigate?.();
  };
  
  return (
    <div 
      className="group flex items-center gap-3 p-3 rounded-sm hover:bg-slate-50 transition-colors cursor-pointer border border-transparent hover:border-slate-200"
      onClick={handleClick}
      data-testid={`tracked-item-${type}-${item.id || item.ticker || item.company_name || item.name || 'unknown'}`}
    >
      {/* Status indicator */}
      <div className={`w-2 h-2 rounded-full ${
        status === "critical" ? "bg-rose-500" : 
        status === "watching" ? "bg-amber-500" : "bg-emerald-500"
      }`} />
      
      {/* Main content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 text-sm truncate">
            {item.name || item.company_name || item.asset_name}
          </span>
          {item.ticker && (
            <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
              {item.ticker}
            </span>
          )}
        </div>
        
        {/* Metadata row */}
        <div className="flex items-center gap-2 mt-0.5">
          {type === "companies" && (item.runway_months || item.cash_runway_months) && (
            <span className={`text-[10px] font-mono flex items-center gap-1 ${
              (item.runway_months || item.cash_runway_months) < 12 ? "text-rose-600" : 
              (item.runway_months || item.cash_runway_months) < 24 ? "text-amber-600" : "text-slate-500"
            }`}>
              <Clock size={10} />
              {item.runway_months || item.cash_runway_months}mo runway
              <InfoTooltip tooltipKey="runway" size="sm" />
            </span>
          )}
          {type === "assets" && item.phase && (
            <span className="text-[10px] font-mono text-blue-600 flex items-center gap-1">
              Phase {item.phase}
              <InfoTooltip tooltipKey="phase" size="sm" />
            </span>
          )}
          {type === "topics" && (
            <span className="text-[10px] font-mono text-emerald-600 flex items-center gap-1">
              <Rss size={10} />
              Filtering Wire news
            </span>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); onRemove?.(item); }}
          className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600"
          title="Remove from tracking"
        >
          <X size={14} />
        </button>
        <ChevronRight size={14} className="text-slate-300" />
      </div>
    </div>
  );
}

/**
 * Status group section with clear labeling
 */
function StatusGroup({ status, items, type, onRemove, onNavigate }) {
  const config = STATUS_GROUPS[status];
  
  if (items.length === 0) return null;
  
  return (
    <div className="mb-4">
      <div className={`flex items-center gap-2 px-3 py-2 rounded-sm ${config.bg} ${config.border} border mb-2`}>
        <config.icon size={14} className={config.color} />
        <div className="flex-1">
          <span className={`text-xs font-semibold ${config.color}`}>
            {config.label}
          </span>
          {config.sublabel && (
            <span className="text-[10px] text-slate-500 ml-2">
              — {config.sublabel}
            </span>
          )}
        </div>
        <span className={`text-xs font-mono ${config.color}`}>
          {items.length}
        </span>
      </div>
      <div className="space-y-1">
        {items.map((item, idx) => (
          <TrackedItem 
            key={item.id || idx} 
            item={item} 
            type={type}
            onRemove={onRemove}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Empty state for a tab - with actionable CTAs
 */
function EmptyState({ type, onNavigateToWatchlist }) {
  const navigate = useNavigate();
  const messages = {
    companies: {
      title: "No companies tracked",
      description: "Start building your watchlist to monitor financial health and news",
      icon: Building2,
      cta: "Add Companies",
      ctaAction: () => navigate("/watchlist")
    },
    assets: {
      title: "No assets tracked", 
      description: "Track drugs and targets to monitor clinical progress",
      icon: Pill,
      cta: "Add Assets",
      ctaAction: () => navigate("/research")
    },
    topics: {
      title: "No news topics",
      description: "Add topics to filter your Monitor news feed",
      icon: Rss,
      cta: "Add Topics",
      ctaAction: () => navigate("/monitor")
    }
  };
  
  const msg = messages[type];
  
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center px-4">
      <div className="w-12 h-12 rounded-sm bg-slate-100 flex items-center justify-center mb-4">
        <msg.icon size={24} className="text-slate-400" />
      </div>
      <p className="font-medium text-slate-900 mb-1">{msg.title}</p>
      <p className="text-sm text-slate-500 max-w-[200px] mb-4">{msg.description}</p>
      <button
        onClick={msg.ctaAction}
        className="px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-sm hover:bg-slate-800 transition-colors"
        data-testid={`empty-state-cta-${type}`}
      >
        {msg.cta}
      </button>
    </div>
  );
}

/**
 * Main Intelligence Hub Component
 */
export default function IntelligenceHub({ isOpen, onClose }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("companies");
  const [searchQuery, setSearchQuery] = useState("");
  const [data, setData] = useState({
    companies: [],
    assets: [],
    topics: []
  });
  const [loading, setLoading] = useState(true);
  
  // Fetch tracked items
  useEffect(() => {
    if (!isOpen) return;
    
    const fetchTrackedItems = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("session_token");
        
        // Fetch company watchlist (from company/health endpoint - same as WatchlistPage)
        let companies = [];
        try {
          const companiesRes = await fetch(`${API}/watchlist/company/health?limit=50`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (companiesRes.ok) {
            const companiesData = await companiesRes.json();
            companies = companiesData.companies || [];
          }
        } catch (e) {
          console.error("Failed to fetch companies:", e);
        }
        
        // Fetch assets watchlist (from /watchlist endpoint)
        let assets = [];
        try {
          const assetsRes = await fetch(`${API}/watchlist`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (assetsRes.ok) {
            const assetsData = await assetsRes.json();
            assets = Array.isArray(assetsData) ? assetsData : assetsData.watchlist || [];
          }
        } catch (e) {
          console.error("Failed to fetch assets:", e);
        }
        
        // Fetch news topics (from /wire/topics endpoint)
        let topics = [];
        try {
          const topicsRes = await fetch(`${API}/wire/topics`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (topicsRes.ok) {
            const topicsData = await topicsRes.json();
            // Transform topics array into objects for consistent rendering
            const topicsArray = topicsData.topics || [];
            topics = topicsArray.map(topic => ({
              name: topic,
              id: topic.toLowerCase().replace(/\s+/g, '-')
            }));
          }
        } catch (e) {
          console.error("Failed to fetch topics:", e);
        }
        
        setData({
          companies,
          assets,
          topics
        });
      } catch (error) {
        console.error("Failed to fetch tracked items:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTrackedItems();
  }, [isOpen]);
  
  // Filter items by search
  const filteredData = {
    companies: data.companies.filter(c => 
      !searchQuery || 
      (c.name || c.company_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.ticker || "").toLowerCase().includes(searchQuery.toLowerCase())
    ),
    assets: data.assets.filter(a =>
      !searchQuery ||
      (a.name || a.asset_name || "").toLowerCase().includes(searchQuery.toLowerCase())
    ),
    topics: data.topics.filter(t =>
      !searchQuery ||
      (t.name || "").toLowerCase().includes(searchQuery.toLowerCase())
    )
  };
  
  // Group items by status
  const groupedItems = {
    critical: filteredData[activeTab].filter(i => categorizeItem(i, activeTab) === "critical"),
    watching: filteredData[activeTab].filter(i => categorizeItem(i, activeTab) === "watching"),
    stable: filteredData[activeTab].filter(i => categorizeItem(i, activeTab) === "stable")
  };
  
  const totalCount = filteredData[activeTab].length;
  const criticalCount = groupedItems.critical.length;
  
  // Handle remove item from watchlist
  const handleRemove = async (item) => {
    const token = localStorage.getItem("session_token");
    
    try {
      if (activeTab === "companies") {
        const companyName = item.company_name || item.name;
        const res = await fetch(`${API}/watchlist/company/${encodeURIComponent(companyName)}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setData(prev => ({
            ...prev,
            companies: prev.companies.filter(c => 
              (c.company_name || c.name) !== companyName
            )
          }));
          toast.success(`Removed ${companyName} from watchlist`);
        }
      } else if (activeTab === "assets") {
        const assetId = item.asset_id || item.id;
        const assetName = item.asset_name || item.name;
        const res = await fetch(`${API}/watchlist/${assetId}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setData(prev => ({
            ...prev,
            assets: prev.assets.filter(a => 
              (a.asset_id || a.id) !== assetId
            )
          }));
          toast.success(`Removed ${assetName} from watchlist`);
        }
      } else if (activeTab === "topics") {
        const topicName = item.name;
        const res = await fetch(`${API}/wire/topics/${encodeURIComponent(topicName)}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setData(prev => ({
            ...prev,
            topics: prev.topics.filter(t => t.name !== topicName)
          }));
          toast.success(`Removed "${topicName}" from topics`);
        }
      }
    } catch (error) {
      console.error("Failed to remove item:", error);
      toast.error("Failed to remove item");
    }
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className="w-[400px] sm:w-[440px] p-0 border-l border-slate-200 shadow-sm"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-10">
          <SheetHeader className="p-4 pb-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2 text-lg font-semibold">
                <Star size={18} className="text-amber-500 fill-amber-500" />
                Intelligence Hub
              </SheetTitle>
              {criticalCount > 0 && (
                <span className="px-2 py-0.5 text-[10px] font-bold bg-rose-100 text-rose-700 rounded-full">
                  {criticalCount} ALERT{criticalCount > 1 ? "S" : ""}
                </span>
              )}
            </div>
          </SheetHeader>
          
          {/* Search */}
          <div className="px-4 py-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search tracked items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
              />
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex px-4 gap-1">
            {TABS.map((tab) => {
              const count = filteredData[tab.id].length;
              const TabIcon = tab.icon;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-t-lg transition-colors ${
                    activeTab === tab.id
                      ? "bg-slate-900 text-white"
                      : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <TabIcon size={14} />
                  {tab.label}
                  {count > 0 && (
                    <span className={`ml-1 px-1.5 py-0.5 text-[10px] rounded-full ${
                      activeTab === tab.id ? "bg-white/20" : "bg-slate-200"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto" style={{ height: "calc(100vh - 180px)" }}>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
            </div>
          ) : totalCount === 0 ? (
            <EmptyState type={activeTab} />
          ) : (
            <>
              <StatusGroup 
                status="critical" 
                items={groupedItems.critical} 
                type={activeTab}
                onRemove={handleRemove}
                onNavigate={onClose}
              />
              <StatusGroup 
                status="watching" 
                items={groupedItems.watching} 
                type={activeTab}
                onRemove={handleRemove}
                onNavigate={onClose}
              />
              <StatusGroup 
                status="stable" 
                items={groupedItems.stable} 
                type={activeTab}
                onRemove={handleRemove}
                onNavigate={onClose}
              />
            </>
          )}
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200">
          <button
            onClick={() => { navigate("/watchlist"); onClose(); }}
            className="w-full py-2.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-sm transition-colors flex items-center justify-center gap-2"
          >
            Open Full Watchlist Manager
            <ExternalLink size={14} />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Trigger button for header
 */
export function IntelligenceHubTrigger({ onClick, alertCount = 0 }) {
  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-sm hover:bg-slate-100 transition-colors group"
      title="Intelligence Hub"
      data-testid="intelligence-hub-trigger"
    >
      <Star 
        size={18} 
        className="text-slate-500 group-hover:text-amber-500 transition-colors" 
      />
      {alertCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
          {alertCount > 9 ? "9+" : alertCount}
        </span>
      )}
    </button>
  );
}
